import React, { useState, useRef } from "react";
import { Lock, LogOut, FileSpreadsheet, Download, UploadCloud, PackagePlus, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { StockForm } from "./StockForm";
import useNoIndex from "../hooks/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUpload() {
  useNoIndex();
  const [key, setKey] = useState(localStorage.getItem("kapatia_admin_key") || "");
  const [verified, setVerified] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bulk");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [singleSaved, setSingleSaved] = useState(null);
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (!key) return;
    fetch(`${API}/enquiries/stats`, { headers: { "X-Admin-Key": key } }).then((r) => {
      if (r.status === 401) {
        localStorage.removeItem("kapatia_admin_key");
        setKey("");
        setVerified(false);
        setError("Password changed. Please sign in again.");
      } else if (r.ok) {
        setVerified(true);
      }
    }).catch(() => {});
  }, [key]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await fetch(`${API}/enquiries/stats`, { headers: { "X-Admin-Key": input } });
      if (!r.ok) { setError("Invalid admin password."); return; }
      localStorage.setItem("kapatia_admin_key", input);
      setKey(input);
      setVerified(true);
    } catch {
      setError("Could not reach server.");
    }
  };
  const logout = () => { localStorage.removeItem("kapatia_admin_key"); setKey(""); setVerified(false); setInput(""); };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setResult(null); setError(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/products/bulk_preview`, { method: "POST", headers: { "X-Admin-Key": key }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not read file");
      setPreview(data);
    } catch (err) {
      setError(err.message); setFile(null);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const confirmImport = async () => {
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const r = await fetch(`${API}/products/bulk_upload`, { method: "POST", headers: { "X-Admin-Key": key }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setResult(data); setPreview(null); setFile(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    const r = await fetch(`${API}/products/template`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kapatia-stock-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!verified) {
    return (
      <div className="grid min-h-screen place-items-center p-5" style={{ background: "var(--cream)" }}>
        <form onSubmit={login} data-testid="upload-login-form" className="w-full max-w-sm rounded-2xl border p-7"
              style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
          <span className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "var(--green-100)", color: "var(--green-800)" }}><Lock size={22} /></span>
          <h1 className="mt-4 font-heading text-[22px] font-bold" style={{ color: "var(--green-950)" }}>Stock Upload</h1>
          <p className="mt-1 text-[14px]" style={{ color: "var(--muted)" }}>
            {key && !error ? "Checking saved session\u2026" : "Enter your admin password."}
          </p>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin password"
                 data-testid="upload-login-input"
                 className="mt-5 w-full rounded-lg border bg-white px-3.5 py-3 outline-none focus:ring-4" style={{ borderColor: "#d8c8af" }} />
          {error && <p className="mt-2 text-[13px] font-semibold" data-testid="upload-login-error" style={{ color: "var(--orange-700)" }}>{error}</p>}
          <button type="submit" data-testid="upload-login-submit" className="mt-4 w-full rounded-full py-3 font-heading font-bold text-white"
                  style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--cream)" }}>
      <header className="border-b" style={{ background: "var(--cream-2)", borderColor: "var(--line)" }}>
        <div className="container flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="flex items-center gap-3">
            <a href="/admin" className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 font-heading text-[12.5px] font-bold"
               data-testid="upload-back-admin" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
              <ArrowLeft size={14} /> Admin
            </a>
            <h1 className="font-heading text-[19px] font-bold" style={{ color: "var(--green-950)" }}>Stock Upload</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border p-1" style={{ borderColor: "var(--line)" }}>
              <button onClick={() => setTab("bulk")} data-testid="upload-tab-bulk"
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13px] font-bold transition-colors"
                      style={tab === "bulk" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
                <FileSpreadsheet size={14} /> Bulk Upload
              </button>
              <button onClick={() => setTab("single")} data-testid="upload-tab-single"
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13px] font-bold transition-colors"
                      style={tab === "single" ? { background: "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
                <PackagePlus size={14} /> Add Single Item
              </button>
            </div>
            <button onClick={logout} data-testid="upload-logout" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-heading text-[13px] font-bold text-white"
                    style={{ background: "var(--green-900)" }}><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl py-8">
        {tab === "single" ? (
          <div className="mx-auto max-w-lg rounded-2xl border p-6" style={{ background: "var(--cream-2)", borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
            {singleSaved ? (
              <div className="grid place-items-center gap-3 py-8 text-center" data-testid="upload-single-success">
                <CheckCircle2 size={48} style={{ color: "var(--green-700)" }} />
                <h2 className="font-heading text-[20px] font-bold" style={{ color: "var(--green-950)" }}>Item #{singleSaved.sno} saved!</h2>
                <button onClick={() => setSingleSaved(null)} data-testid="upload-single-add-another"
                        className="mt-1 inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading font-bold text-white"
                        style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
                  <PackagePlus size={16} /> Add Another
                </button>
              </div>
            ) : (
              <StockForm headers={{ "X-Admin-Key": key }} onSuccess={setSingleSaved} />
            )}
          </div>
        ) : (
          <div className="grid gap-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-5"
                 style={{ background: "var(--cream-2)", borderColor: "var(--line)" }}>
              <div>
                <h2 className="font-heading text-[16px] font-bold" style={{ color: "var(--green-950)" }}>Need the correct format?</h2>
                <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>Columns: Sno, Type, Code, Name, Brand, Qty, Price — .csv or .xlsx</p>
              </div>
              <button onClick={downloadTemplate} data-testid="upload-download-template"
                      className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-heading text-[13.5px] font-bold"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)", background: "#fff" }}>
                <Download size={15} style={{ color: "#E8501A" }} /> Download Template
              </button>
            </div>

            {result && (
              <div className="flex items-center gap-3 rounded-2xl border p-5" data-testid="upload-result"
                   style={{ background: "var(--green-100)", borderColor: "var(--line)" }}>
                <CheckCircle2 size={26} style={{ color: "var(--green-700)" }} />
                <p className="font-heading text-[15px] font-bold" style={{ color: "var(--green-950)" }}>
                  Imported {result.imported} items ({result.mode === "replace" ? "replaced all stock" : "added to existing"}). Total in inventory: {result.total}.
                </p>
              </div>
            )}

            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={pickFile} className="hidden" data-testid="upload-file-input" />
            {!preview && (
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-pick-file"
                      className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed py-14 font-heading font-bold disabled:opacity-60"
                      style={{ borderColor: "#d8c8af", color: "var(--green-800)", background: "rgba(255,255,255,0.5)" }}>
                {busy ? <Loader2 size={30} className="animate-spin" /> : <UploadCloud size={30} style={{ color: "#E8501A" }} />}
                <span className="text-[16px]">{busy ? "Reading file…" : "Click to choose Excel or CSV file"}</span>
                <span className="text-[12.5px] font-semibold" style={{ color: "var(--muted)" }}>You'll see a preview before anything is imported</span>
              </button>
            )}

            {error && <p className="text-[14px] font-semibold" data-testid="upload-error" style={{ color: "var(--orange-700)" }}>{error}</p>}

            {preview && (
              <div className="grid gap-4" data-testid="upload-preview">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-heading text-[15.5px] font-bold" style={{ color: "var(--green-950)" }}>
                    Preview — {preview.count} items found in <span style={{ color: "#E8501A" }}>{file?.name}</span>
                  </p>
                  <div className="flex items-center gap-2 rounded-full border p-1" style={{ borderColor: "var(--line)", background: "#fff" }}>
                    {[{ k: "append", l: "Add to existing" }, { k: "replace", l: "Replace all stock" }].map((m) => (
                      <button key={m.k} onClick={() => setMode(m.k)} data-testid={`upload-mode-${m.k}`}
                              className="rounded-full px-4 py-1.5 font-heading text-[12.5px] font-bold transition-colors"
                              style={mode === m.k ? { background: m.k === "replace" ? "var(--orange-700)" : "var(--green-900)", color: "#fff" } : { color: "var(--green-900)" }}>
                        {m.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto rounded-2xl border" style={{ background: "#fff", borderColor: "var(--line)" }}>
                  <table className="w-full min-w-[700px] text-left text-[13.5px]">
                    <thead>
                      <tr style={{ background: "var(--green-100)", color: "var(--green-950)" }} className="font-heading text-[12px] uppercase tracking-wide">
                        <th className="px-4 py-2.5">Sno</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Name</th>
                        <th className="px-4 py-2.5">Brand</th><th className="px-4 py-2.5">Qty</th><th className="px-4 py-2.5">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.items.slice(0, 50).map((it, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: "var(--line)", color: "#31463a" }}>
                          <td className="px-4 py-2">{it.sno || "-"}</td>
                          <td className="px-4 py-2">{it.type || "-"}</td>
                          <td className="max-w-[320px] px-4 py-2 font-semibold" style={{ color: "var(--green-950)" }}>{it.name}</td>
                          <td className="px-4 py-2">{it.brand || "-"}</td>
                          <td className="px-4 py-2">{it.qty}</td>
                          <td className="px-4 py-2">₹{(it.price || 0).toLocaleString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.items.length > 50 && (
                    <p className="px-4 py-2.5 text-[12.5px]" style={{ color: "var(--muted)" }}>…and {preview.count - 50} more rows</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <button onClick={confirmImport} disabled={busy} data-testid="upload-confirm-import"
                          className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-heading text-[14.5px] font-bold text-white disabled:opacity-60"
                          style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                    Confirm Import ({preview.count} items{mode === "replace" ? " — replaces everything" : ""})
                  </button>
                  <button onClick={() => { setPreview(null); setFile(null); }} data-testid="upload-cancel-preview"
                          className="rounded-full border px-6 py-3 font-heading text-[14.5px] font-bold"
                          style={{ borderColor: "var(--line)", color: "var(--green-900)", background: "#fff" }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
